import { BomItemController } from 'controllers/Project/BomItemController'
import { useController } from 'customHooks/useController'
import { useLocalStorage } from 'customHooks/useLocalStorage'
import _ from 'lodash'
import { MultiplyMoney, SumMoney } from 'model/Money'
import {
  AssemblyHeaderRow,
  AssemblyRow,
  PartTypeRow,
} from 'model/Project/BoMItemRow'
import { SumQuantity } from 'model/Quantity'
import { useCallback, useEffect, useState } from 'react'
import { useAppSelector } from 'store/configureStore'
import {
  CategoryDto,
  ManuallySetAdditionalCostsArgs,
  ManuallySetProductionTimeArgs,
  MoneyDto,
  PriceRowDto,
  QuantityDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { store } from 'store/configureStore'
import { bomItemFinancialSelector } from 'store/Project/ProjectSelectors'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'

export type TargetPriceGraphData = {
  id: string
  description: string
  resource?: string
  productionTime?: QuantityDto
  primaryWorkingStep?: WorkingStepType
  secondaryWorkingStep?: WorkingStepType
  loading?: MoneyDto
  mainActivityCostPrice?: MoneyDto
  unloading?: MoneyDto
  surchargeValue?: MoneyDto
  discountValue?: MoneyDto
  setup?: MoneyDto
  childrens?: Record<string, Omit<TargetPriceGraphData, 'childrens'>>
  total?: MoneyDto
  color?: string
  salesPrice?: MoneyDto
  sharedCost?: boolean
  matchDescription?: string
  totalQuantity?: QuantityDto
  staggeredTotalQuantity?: QuantityDto
}

export function useFinancialDetails(props: {
  open?: boolean
  bomItemId: string
}) {
  const { partyId, projectId } = useOrganizationAndProjectContext()

  const { controller, loading, setLoading } = useController(
    () => new BomItemController(partyId, projectId)
  )

  const [summarizedPriceDetails, setSummarizedPriceDetails] =
    useState<TargetPriceGraphData[]>(undefined)

  const [priceDetails, setPriceDetails] = useState<PriceRowDto[]>(undefined)
  const [formDetails, setFormDetails] = useState<CategoryDto[]>(undefined)

  const bomItemFinancial = useAppSelector(
    bomItemFinancialSelector(props.bomItemId),
    _.isEqual
  )

  useEffect(() => {
    setPriceDetails(undefined)
    setSummarizedPriceDetails(undefined)
    setFormDetails(undefined)
    setLoading({ 'get-item-details': true })
  }, [props.bomItemId, setLoading])

  const getSumOfChildrens = useCallback(
    (bomItemId: string, unit: 'perUnit' | 'total'): MoneyDto => {
      const bomItemType =
        store.getState().project.activeProject?.id === bomItemId
          ? 'Project'
          : store.getState().project.flatView[bomItemId]?.type ||
            store.getState().project.treeView[bomItemId]?.type

      if (bomItemType === 'Project') {
        const childBomItems = Object.values(
          store.getState().project.flatView
        ).filter(
          (x) => x.parentId === x.id && x.type === 'AssemblyHeader'
        ) as AssemblyHeaderRow[]

        const sumOfPartsSalesPrice = _.sumBy(childBomItems, (x) =>
          unit === 'perUnit'
            ? x.assembly.financial?.salesPricePerItem?.value
            : x.assembly.financial?.salesPriceOfItems?.value
        )

        if (!childBomItems.length) return null

        return {
          ...childBomItems[0].assembly.financial?.salesPricePerItem,
          value: sumOfPartsSalesPrice,
        }
      } else if (bomItemType === 'AssemblyHeader') {
        const childBomItems: PartTypeRow[] = Object.values(
          store.getState().project.flatView
        ).filter((x) => x.parentId === bomItemId) as PartTypeRow[]

        const sumOfPartsSalesPrice = _.sumBy(childBomItems, (x) =>
          unit === 'perUnit'
            ? x.financial?.salesPricePerItem?.value
            : x.financial?.salesPriceOfItems?.value
        )

        return {
          ...childBomItems[0].financial.salesPricePerItem,
          value: sumOfPartsSalesPrice,
        }
      }

      return {
        value: 0,
      }
    },
    []
  )

  const getChildItems = useCallback(
    (bomItemId: string, unit: 'perUnit' | 'total'): TargetPriceGraphData => {
      if (!store.getState().project.activeProject) {
        return null
      }

      const bomItemType =
        store.getState().project.activeProject?.id === bomItemId
          ? 'Project'
          : store.getState().project.flatView[bomItemId]?.type ||
            store.getState().project.treeView[bomItemId]?.type

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let childBomItems: any[]

      if (bomItemType === 'Project') {
        childBomItems = Object.values(store.getState().project.flatView)
          .filter((x) => x.parentId === x.id && x?.type === 'AssemblyHeader')
          .map((h) => h['assembly'] as AssemblyRow)
      } else if (bomItemType === 'AssemblyHeader') {
        childBomItems = Object.values(store.getState().project.flatView).filter(
          (x) =>
            x?.parentId === bomItemId &&
            x?.type === 'Row' &&
            x?.financial.salesPricePerItem?.value > 0
        ) as PartTypeRow[]
      } else if (bomItemType === 'Assembly') {
        childBomItems = Object.values(store.getState().project.treeView).filter(
          (x) =>
            x.parentId === bomItemId &&
            x?.type === 'Row' &&
            x.financial.salesPricePerItem?.value > 0
        ) as PartTypeRow[]
      }

      if (!childBomItems) return null

      const childrens = childBomItems
        .sort(
          (a, b) =>
            b.financial?.salesPricePerItem.value -
            a.financial?.salesPricePerItem?.value
        )
        .reduce(
          (p, c, i) => ({
            ...p,
            [i]: {
              id: c.id,
              description: c.name,
              primary:
                unit === 'perUnit'
                  ? c.financial?.salesPricePerItem
                  : c.financial?.salesPriceOfItems,
              salesPrice:
                unit === 'perUnit'
                  ? c.financial?.salesPricePerItem
                  : c.financial?.salesPriceOfItems,
              // color: chroma.random().hex(),
              color: 'transparent',
            },
          }),
          {}
        )

      return {
        id: undefined,
        description: bomItemType === 'Project' ? 'assemblies' : 'parts',
        childrens: childrens,
        total: getSumOfChildrens(bomItemId, unit),
        salesPrice: undefined,
        sharedCost: false,
        matchDescription: undefined,
      }
    },
    [getSumOfChildrens]
  )

  const getPriceDataForGraph = useCallback(
    (
      details: PriceRowDto[],
      bomItemId: string,
      unit: 'perUnit' | 'total'
    ): TargetPriceGraphData[] => {
      const targetPriceGraphData = details.filter((x) => !x.isTotalRow)

      const data: TargetPriceGraphData[] = []
      data.push(getChildItems(bomItemId, unit))

      data.push(
        ...targetPriceGraphData.reduce(
          (acc: TargetPriceGraphData[], curr, index, all) => {
            if (curr?.isWorkingStep) {
              if (
                acc.findIndex(
                  (x) =>
                    x.primaryWorkingStep === curr.workingStep.primaryWorkingStep
                ) > -1
              ) {
                return acc
              }

              const workingStepActions = all.filter(
                (x) =>
                  x.workingStep?.primaryWorkingStep ===
                  curr.workingStep.primaryWorkingStep
              )

              acc.push({
                id: bomItemId,
                description: curr.workingStep.primaryWorkingStep,
                resource: curr.workingStep.resource.name,
                sharedCost: curr.costsAreShared,
                matchDescription: curr.workingStep.resource.matchDescription,
                productionTime: SumQuantity(
                  workingStepActions.map((x) => x.productionTime)
                ),
                total: SumMoney(workingStepActions.map((x) => x.salesPrice)),
                primaryWorkingStep: curr.workingStep.primaryWorkingStep,
                salesPrice: curr.salesPrice,
                mainActivityCostPrice: workingStepActions.find(
                  (x) =>
                    x.workingStep.secondaryWorkingStep ===
                    curr.workingStep.primaryWorkingStep
                )?.costPrice,
                setup: workingStepActions.find(
                  (x) =>
                    x.workingStep.secondaryWorkingStep === WorkingStepType.Setup
                )?.costPrice,
                loading: workingStepActions.find(
                  (x) =>
                    x.workingStep.secondaryWorkingStep ===
                    WorkingStepType.Loading
                )?.costPrice,
                unloading: workingStepActions.find(
                  (x) =>
                    x.workingStep.secondaryWorkingStep ===
                    WorkingStepType.Unloading
                )?.costPrice,
                surchargeValue: SumMoney(
                  workingStepActions.map((x) => x.surchargeValue)
                ),
                discountValue: SumMoney(
                  workingStepActions.map((x) => ({
                    ...x.discountValue,
                    value: x.discountValue.value * -1,
                  }))
                ),
              })
            } else {
              acc.push({
                id: `${bomItemId}`,
                description: 'material',
                mainActivityCostPrice: curr?.costPrice,

                surchargeValue: curr?.surchargeValue,
                discountValue: {
                  ...curr?.discountValue,
                  value: curr?.discountValue.value * -1,
                },
                total: curr?.salesPrice,
                salesPrice: curr?.salesPrice,
                sharedCost: false,
                matchDescription: undefined,
              })
            }
            return acc
          },
          []
        )
      )

      const totalRow = details.find((x) => x.isTotalRow)

      data.push({
        id: undefined,
        description: 'total',
        mainActivityCostPrice: totalRow?.costPrice,
        surchargeValue: totalRow?.surchargeValue,
        discountValue: {
          ...(totalRow?.discountValue || {}),
          value: totalRow?.discountValue.value * -1,
        },
        salesPrice: totalRow?.salesPrice,
        sharedCost: totalRow?.costsAreShared,
        matchDescription: undefined,
      })

      return data.filter((x) => Boolean(x))
    },
    [getChildItems]
  )

  const multiplyUnitPriceByTotalProjectQuantity = useCallback(
    (priceDetails) => {
      if (!priceDetails) return null

      return {
        ...priceDetails,
        productionTime: MultiplyMoney(
          priceDetails.productionTime,
          bomItemFinancial.totalProjectQuantity
        ),
        productionTimeCosts: MultiplyMoney(
          priceDetails.productionTimeCosts,
          bomItemFinancial.totalProjectQuantity
        ),
        productionTimeManuallySet: MultiplyMoney(
          priceDetails.productionTimeManuallySet,
          bomItemFinancial.totalProjectQuantity
        ),
        additionalProductionCosts: MultiplyMoney(
          priceDetails.additionalProductionCosts,
          bomItemFinancial.totalProjectQuantity
        ),
        additionalProductionCostsManuallySet: MultiplyMoney(
          priceDetails.additionalProductionCostsManuallySet,
          bomItemFinancial.totalProjectQuantity
        ),
        costPrice: MultiplyMoney(
          priceDetails?.costPrice,
          bomItemFinancial?.totalProjectQuantity
        ),
        surchargeValue: MultiplyMoney(
          priceDetails.surchargeValue,
          bomItemFinancial.totalProjectQuantity
        ),
        discountValue: MultiplyMoney(
          priceDetails.discountValue,
          bomItemFinancial.totalProjectQuantity
        ),
        salesPrice: MultiplyMoney(
          priceDetails.salesPrice,
          bomItemFinancial.totalProjectQuantity
        ),
      } as PriceRowDto
    },
    [bomItemFinancial?.totalProjectQuantity]
  )

  const [financialViewUnit, setFinancialViewUnit] = useLocalStorage<
    'perUnit' | 'total'
  >('project:financial-scope', 'total')
  const handleChangeFinancialView = useCallback(
    (unit: 'perUnit' | 'total') => {
      if (!bomItemFinancial) {
        return null
      }

      setFinancialViewUnit(unit)

      if (unit === 'total') {
        setPriceDetails((state) => {
          const newState = state.map((x) =>
            x.isBatchIndependent
              ? x
              : multiplyUnitPriceByTotalProjectQuantity(x)
          )

          setPriceDetails(newState)
          setSummarizedPriceDetails(
            getPriceDataForGraph(newState, props.bomItemId, unit)
          )

          return newState
        })
      }
    },
    [
      bomItemFinancial,
      getPriceDataForGraph,
      multiplyUnitPriceByTotalProjectQuantity,
      props.bomItemId,
      setFinancialViewUnit,
    ]
  )

  const getItemDetails = useCallback(async () => {
    try {
      const rowDetails = await controller.GetItemDetails(props.bomItemId)

      // batch independent items are stored as unit prices in the backend
      // we need to show the total value of it in the frontend
      rowDetails.prices = rowDetails.prices.map((x) =>
        x.isBatchIndependent ? multiplyUnitPriceByTotalProjectQuantity(x) : x
      )

      // setTimeout(() => {
      setPriceDetails(rowDetails.prices)
      setFormDetails(rowDetails.categories)

      setSummarizedPriceDetails(
        getPriceDataForGraph(
          rowDetails.prices,
          props.bomItemId,
          financialViewUnit
        )
      )
      handleChangeFinancialView(financialViewUnit)
      // })
    } catch (err) {
      ShowException('row details', err)
    }
  }, [
    controller,
    props.bomItemId,
    getPriceDataForGraph,
    financialViewUnit,
    multiplyUnitPriceByTotalProjectQuantity,
    handleChangeFinancialView,
  ])

  useEffect(() => {
    // if (props.open && financialViewUnit === 'perUnit') {
    let getItemDetailsTimeout = null

    if (props.open) {
      getItemDetailsTimeout = setTimeout(getItemDetails, 640)
    } else {
      clearTimeout(getItemDetailsTimeout)
      controller.CancelRequests()
    }
    return () => {
      clearTimeout(getItemDetailsTimeout)
      controller.CancelRequests()
    }
  }, [controller, getItemDetails, props.open])

  const handleResetProductionTime = useCallback(
    async (
      rowId: string,
      primaryWorkingStep: WorkingStepType,
      secondaryWorkingStep: WorkingStepType
    ) => {
      try {
        await controller.DeleteManualSetProductionTime(
          rowId,
          primaryWorkingStep,
          secondaryWorkingStep
        )
        // props.onEditFinished && props.onEditFinished(rowData)
      } catch (ex) {
        ShowException('project', ex)
      }
    },
    [controller]
  )

  const handleResetAdditionalCosts = useCallback(
    async (
      rowId: string,
      primaryWorkingStep: WorkingStepType,
      secondaryWorkingStep: WorkingStepType
    ) => {
      try {
        await controller.DeleteManualSetAdditionalCosts(
          rowId,
          primaryWorkingStep,
          secondaryWorkingStep
        )
      } catch (ex) {
        ShowException('reset additional costs', ex)
      }
    },
    [controller]
  )

  const handleSaveProductionTime = useCallback(
    async (rowId: string, args: ManuallySetProductionTimeArgs) => {
      try {
        await controller.ManualSetProductionTime(rowId, args)
        setPriceDetails((current) => {
          return current.map((x) =>
            x.workingStep.primaryWorkingStep === args.primaryWorkingStep &&
            x.workingStep.secondaryWorkingStep === args.secondaryWorkingStep
              ? { ...x, productionTimeManuallySet: args.productionTime }
              : x
          )
        })

        return Promise.resolve()
      } catch (ex) {
        ShowException('save production time', ex)
      }
    },
    [controller]
  )

  const handleSaveAdditionalCosts = useCallback(
    async (rowId: string, args: ManuallySetAdditionalCostsArgs) => {
      try {
        await controller.ManualSetAdditionalCosts(rowId, args)
        setPriceDetails((current) => {
          return current.map((x) =>
            x.workingStep.primaryWorkingStep === args.primaryWorkingStep &&
            x.workingStep.secondaryWorkingStep === args.secondaryWorkingStep
              ? {
                  ...x,
                  additionalProductionCostsManuallySet: args.additionalCosts,
                }
              : x
          )
        })
      } catch (ex) {
        ShowException('save additional costs', ex)
      }
    },
    [controller]
  )

  return {
    getPriceDataForGraph,
    summarizedPriceDetails,
    priceDetails,
    loading,
    controller,
    getItemDetails,
    setLoading,
    financialViewUnit,
    handleChangeFinancialView,
    totalProjectQuantity: bomItemFinancial?.totalProjectQuantity,
    bomItemFinancial,
    handleResetProductionTime,
    handleResetAdditionalCosts,
    handleSaveProductionTime,
    handleSaveAdditionalCosts,
    formDetails,
    multiplyUnitPriceByTotalProjectQuantity,
    clearPriceDetails: () => setPriceDetails(undefined),
  }
}
